import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { IconX, IconLoader2, IconAlertTriangle } from '@tabler/icons-react';
import type {
  JobDeletionGroup,
  JobDeletionPreview,
  JobDeletionResult,
} from '@techgitter/contracts';
import { deleteJob, getJobDeletionPreview } from '../../services/workItemDataService';
import '../check-in/sessionDetail/dialogs/checkInDialog.css';
import './JobDeleteDialog.css';

export interface JobDeleteDialogProps {
  open: boolean;
  /** The job being deleted. `id` is the RepairShopr ticket id. */
  job: { guid: string; id?: string; number?: string | number; title?: string } | null;
  onClose: () => void;
  onDeleted: (result: JobDeletionResult) => void;
}

/**
 * The one confirmation for deleting a job, from every entry point (dashboard
 * row, ticket page). A destructive confirmation → modal (sm4rt-web-ui §6),
 * on the shared `checkInDialog.css` shell.
 *
 * It renders exactly what the server's preview returns: what the delete will
 * remove, what it will leave, and anything that blocks it. The server omits
 * empty categories, so nothing here decides what to mention — a job with no
 * quotes never says "quotes". A paid invoice blocks the delete outright: the
 * dialog lists it and offers no Delete button.
 *
 * The delete sends the preview's fingerprint back. If the list changed in
 * between, the server refuses with a fresh preview and the dialog redraws
 * from it, so what the user confirmed is what gets removed.
 */
export const JobDeleteDialog: React.FC<JobDeleteDialogProps> = ({ open, job, onClose, onDeleted }) => {
  const [preview, setPreview] = useState<JobDeletionPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !job) return;
    let cancelled = false;
    setPreview(null);
    setError(null);
    setNotice(null);
    setReason('');
    setSubmitting(false);
    setLoading(true);
    getJobDeletionPreview(job.guid)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, job]);

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  if (!open || !job) return null;

  const handleDelete = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const outcome = await deleteJob(job.guid, {
        ticketId: job.id,
        fingerprint: preview.fingerprint,
        reason,
      });
      if (outcome.ok) {
        // Closing unmounts the content; no state to reset after this.
        onDeleted(outcome.result);
        onClose();
        return;
      }
      setPreview(outcome.conflict.preview);
      setNotice(
        outcome.conflict.code === 'stale'
          ? 'What this delete would remove has changed. Review the list below and confirm again.'
          : outcome.conflict.error,
      );
      setSubmitting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete the job');
      setSubmitting(false);
    }
  };

  const number = preview?.job.number || job.number || job.id;
  const blocked = !!preview && !preview.canDelete;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="ci-dialog job-del" role="dialog" aria-modal="true" aria-label="Delete ticket">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Delete ticket #{number}</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body job-del__body">
          {loading && (
            <div className="job-del__loading">
              <IconLoader2 size={16} stroke={1.5} className="ci-dialog__spin" />
              Checking what this ticket is linked to…
            </div>
          )}

          {notice && <div className="job-del__notice">{notice}</div>}

          {preview && blocked && (
            <>
              <div className="job-del__blocked">
                <IconAlertTriangle size={16} stroke={1.5} className="job-del__blocked-icon" />
                <div>
                  This ticket can&rsquo;t be deleted while an invoice on it has payments
                  against it. Mark the invoice unpaid first — deleting the ticket never
                  touches a paid invoice.
                </div>
              </div>
              {preview.blocking.map((g) => (
                <GroupList key={g.category} group={g} onInvoiceLink={onClose} />
              ))}
            </>
          )}

          {preview && !blocked && (
            <>
              <Section title="Will be deleted" groups={preview.willDelete} />
              {preview.willNotDelete.length > 0 && (
                <Section title="Will not be deleted" groups={preview.willNotDelete} muted />
              )}
              <div>
                <label className="ci-dialog__field-label" htmlFor="job-del-reason">
                  Reason (optional)
                </label>
                <textarea
                  id="job-del-reason"
                  className="ci-dialog__textarea job-del__reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Recorded in the audit trail"
                  disabled={submitting}
                />
              </div>
            </>
          )}

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button
            type="button"
            className="ci-dialog__cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            {blocked ? 'Close' : 'Keep ticket'}
          </button>
          {!blocked && (
            <button
              type="button"
              className="ci-dialog__confirm ci-dialog__confirm--danger"
              onClick={handleDelete}
              disabled={!preview || loading || submitting}
            >
              {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
              {submitting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const Section: React.FC<{ title: string; groups: JobDeletionGroup[]; muted?: boolean }> = ({
  title,
  groups,
  muted,
}) => (
  <div className={`job-del__section${muted ? ' job-del__section--muted' : ''}`}>
    <div className="job-del__section-title">{title}</div>
    {groups.map((g) => (
      <GroupList key={g.category} group={g} />
    ))}
  </div>
);

/** Invoice numbers live in the reference ("Invoice #261905"); the page is /invoice/:number. */
const invoiceNumber = (reference: string): string | null => {
  const match = /#(\d+)/.exec(reference);
  return match ? match[1] : null;
};

const GroupList: React.FC<{ group: JobDeletionGroup; onInvoiceLink?: () => void }> = ({
  group,
  onInvoiceLink,
}) => (
  <div className="job-del__group">
    {group.category !== 'job' && (
      <div className="job-del__group-label">
        {group.label}
        <span className="job-del__count">{group.items.length}</span>
      </div>
    )}
    <ul className="job-del__items">
      {group.items.map((item) => {
        const linkTo = onInvoiceLink ? invoiceNumber(item.reference) : null;
        return (
          <li key={item.id} className="job-del__item">
            <div className="job-del__item-line">
              {linkTo ? (
                <RouterLink
                  className="job-del__ref job-del__ref--link"
                  to={`/invoice/${linkTo}`}
                  onClick={onInvoiceLink}
                >
                  {item.reference}
                </RouterLink>
              ) : (
                <span className="job-del__ref">{item.reference}</span>
              )}
              {item.description && <span className="job-del__desc">{item.description}</span>}
            </div>
            {item.note && <div className="job-del__note">{item.note}</div>}
          </li>
        );
      })}
    </ul>
  </div>
);
